// Hideo - the plan (PED): Board, Calendar and Timeline share the saved-plan tabs, the Type and format filters and the
// visible videos (logic without UI). State in src/stores/ped.ts; pieces in src/components/PedViews.vue, PedFilters.vue;
// the Board is src/pages/BoardPage.vue.
// - Saved plans (data.settings.pedViews): name + ticked projects; the plan chosen on this device is in localStorage
//   "creatorOS_pedview" = { <profile id>: <plan id> } ('' or a missing plan = the active project).
// - Filters: Type ('' = all, otherwise a fixed Type name; device preference "creatorOS_pedtype") and format
//   ('' = all, 'none' = no format, otherwise 'n:<lower-case name>', src/features/formats.ts).
// - Published videos are not on the Board (Published videos page): counts leave them out.
import { t, tv } from '../i18n'
import { DEF_FORMATS, STATUS_KEYS, STATUS_NEUTRAL, STATUS_READY, fixStatus, fixedType, formatKind, kindStatuses, statusName } from '../data/contentTypes'
import type { FormatName, Kind, PedView, ProfileData, Project, Script, Series, StatusKey, Video } from '../data/types'
import { tlEnd } from './agenda'
import { FORMAT_NONE, formatGroups, seriesKey, seriesOf, type FormatGroup } from './formats'
import { ALL_PROJECTS } from './projects'
import { videoScripts } from './scripts'
import { togglePill } from './dialogs'

export const PEDVIEW_KEY = 'creatorOS_pedview'
export const PEDTYPE_KEY = 'creatorOS_pedtype'

/** Filters of Board, Calendar and Timeline (state in src/stores/ped.ts) */
export interface PedFilter {
  /** '' = all, 'none' = no format, otherwise 'n:<lower-case name>' */
  series: string
  /** '' = all, otherwise a Type name */
  type: FormatName | ''
}

/** Stored Type filter: only a fixed Type name counts */
export const parseTypeFilter = (raw: string | null): FormatName | '' => (DEF_FORMATS.includes(raw as FormatName) ? raw as FormatName : '')


/** The chosen plan, if it still exists and has projects; null = the active project */
export function resolvePedView(d: ProfileData, id: string | undefined): PedView | null {
  const v = (d.settings.pedViews || []).find(x => x.id === id)
  return v && v.projects.length ? v : null
}

/** Items of a plan's projects */
export const inPedView = <T extends { projectId?: string }>(view: PedView, list: T[] | null | undefined): T[] =>
  (list || []).filter(x => view.projects.includes(x.projectId as string))

/** Projects shown with a plan, or with the active project ('all' = every project) */
export const visibleProjects = (d: ProfileData, view: PedView | null, active: string): Project[] =>
  view ? d.projects.filter(p => view.projects.includes(p.id)) : active === ALL_PROJECTS ? d.projects : d.projects.filter(p => p.id === active)

// ===== "Plan" dialog (PedViewDialog.vue): name + ticked projects =====
/** Longest plan name */
export const PEDVIEW_NAME_MAX = 30
/** Starting fields of the dialog: the saved plan, or a new one ('' or unknown id) with every project ticked */
export function pedViewForm(d: ProfileData, id: string): PedView {
  const v = (d.settings.pedViews || []).find(x => x.id === id)
  return v ? { id: v.id, name: v.name, projects: [...v.projects] } : { id: '', name: '', projects: d.projects.map(p => p.id) }
}
/** Ticks or unticks a project; the last ticked one stays (a plan has at least one project) */
export const togglePedViewProject = (projects: readonly string[], id: string): string[] => togglePill(projects, id, true)
/** Saves the plan (projects in the profile's order) and returns its id; null with an empty name. The caller saves. */
export function savePedView(d: ProfileData, form: PedView, now = Date.now()): string | null {
  const name = String(form.name || '').trim().slice(0, PEDVIEW_NAME_MAX)
  if (!name) return null
  const views = d.settings.pedViews || (d.settings.pedViews = [])
  const projects = d.projects.map(p => p.id).filter(id => form.projects.includes(id))
  const v = form.id ? views.find(x => x.id === form.id) : undefined
  if (v) { v.name = name; v.projects = projects; return v.id }
  if (form.id) return form.id   // plan deleted meanwhile: nothing to save (like before)
  const id = 'pv_' + now
  views.push({ id, name, projects })
  return id
}
/** Removes a saved plan (videos and projects are not touched). The caller saves. */
export function deletePedView(d: ProfileData, id: string): void {
  d.settings.pedViews = (d.settings.pedViews || []).filter(x => x.id !== id)
}

/** Videos of the plan or project (already scoped) that pass the Type and format filters */
export function filterVideos(d: ProfileData, scoped: Video[], f: PedFilter): Video[] {
  let videos = scoped
  if (f.type) videos = videos.filter(v => fixedType(v.format) === f.type)
  if (!f.series) return videos
  if (f.series === FORMAT_NONE) return videos.filter(v => !seriesOf(d, v.seriesId))
  return videos.filter(v => { const s = seriesOf(d, v.seriesId); return !!s && seriesKey(s) === f.series })
}

/** Format filter to keep: '' when its pill is gone (format deleted or renamed, other plan) */
export const validSeriesFilter = (filter: string, scopedSeries: Series[]): string =>
  !filter || filter === FORMAT_NONE || formatGroups(scopedSeries).some(g => g.key === filter) ? filter : ''

// ===== Filter bar =====
interface TypeChip { type: FormatName | ''; count: number; kind: Kind | null }
interface FormatChip { key: string; count: number; group: FormatGroup | null }
interface PedFiltersView {
  /** Row of Types: shown when more than one Type is used, or when the filter is on */
  types: TypeChip[]
  /** "All" + one pill per format name + "No format" (only with formats and videos without one) */
  formats: FormatChip[]
}
/** The filter bar: counts of the videos in production (published ones left out) of the plan or project */
export function pedFiltersView(d: ProfileData, scopedVideos: Video[], scopedSeries: Series[], f: PedFilter): PedFiltersView {
  const all = scopedVideos.filter(v => !v.published), groups = formatGroups(scopedSeries)
  const tCount = (type: FormatName) => all.filter(v => fixedType(v.format) === type).length
  const used = DEF_FORMATS.filter(type => tCount(type) || f.type === type)
  const types: TypeChip[] = used.length > 1 || f.type
    ? [{ type: '', count: all.length, kind: null }, ...used.map(type => ({ type, count: tCount(type), kind: formatKind(type) }))]
    : []
  const allF = f.type ? all.filter(v => fixedType(v.format) === f.type) : all
  const noneF = allF.filter(v => !seriesOf(d, v.seriesId)).length
  const formats: FormatChip[] = [{ key: '', count: allF.length, group: null },
    ...groups.map(g => ({ key: g.key, count: allF.filter(v => g.ids.includes(v.seriesId as string)).length, group: g })),
    ...(groups.length && noneF ? [{ key: FORMAT_NONE, count: noneF, group: null }] : [])]
  return { types, formats }
}

// ===== Board =====
/** Board columns: the five stored statuses, with their icon */
const COLUMN_ICONS = ['fa-lightbulb', 'fa-file-lines', 'fa-video', 'fa-scissors', 'fa-circle-check']
const BOARD_COLUMNS: readonly (readonly [StatusKey, string])[] = STATUS_KEYS.map((st, i) => [st, COLUMN_ICONS[i]] as const)

/** Column name: with a Type filter, that Type's words; without one, neutral names (even if all videos have the same
 *  Type). Stored status values never change. Also the Timeline's group headers */
export const columnName = (type: string, st: string): string =>
  type ? statusName(formatKind(type), st) : (STATUS_NEUTRAL[(STATUS_KEYS as readonly string[]).indexOf(st)] || st)
/** With a Type that has fewer steps (Blog) the columns it does not use are hidden */
export const columnHidden = (type: string, st: string): boolean => !!type && !(kindStatuses(formatKind(type)) as readonly string[]).includes(st)

interface BoardColumn {
  status: StatusKey
  icon: string
  name: string
  hidden: boolean
  videos: Video[]
}
interface BoardView {
  columns: BoardColumn[]
  /** Videos shown / videos in production of the plan or project */
  shown: number
  total: number
  /** "12", or "3 of 12" with filters */
  totalText: string
}
/** The Board: visible videos (filters on, published ones left out) in the column of their status, in data order.
 *  A video with an unknown status is in no column (but in the counts) */
export function boardView(d: ProfileData, scopedVideos: Video[], f: PedFilter): BoardView {
  const total = scopedVideos.filter(v => !v.published).length
  const shown = filterVideos(d, scopedVideos, f).filter(v => !v.published)
  return {
    columns: BOARD_COLUMNS.map(([status, icon]) => ({ status, icon, name: columnName(f.type, status), hidden: columnHidden(f.type, status), videos: shown.filter(v => v.status === status) })),
    shown: shown.length, total,
    totalText: shown.length === total ? String(total) : t('{n} of {total}', { n: shown.length, total })
  }
}

/** Shown title of a video: a stored default ("Untitled" in either language) in the active language */
export const videoTitle = (v: Pick<Video, 'title'>): string => String(tv(v.title) || t('Untitled'))

/** Ticked checklist items of a video, out of the items in Settings */
export function checklistProgress(d: ProfileData, v: Video): [number, number] {
  const items = d.settings.checklist, chk = v.checklist || {}
  return [items.filter(it => chk[it.id]).length, items.length]
}

/** The script picked in the video dialog (or the video's first one) and how many other scripts the video has */
export function cardScript(d: ProfileData, v: Video): { script: Script | null; more: number } {
  const all = videoScripts(d, v)
  return { script: all.find(x => x.id === v.scriptId) || all[0] || null, more: Math.max(all.length - 1, 0) }
}

/** Overdue: the end of work (publish day) has passed and the video is not ready */
export const isLate = (v: Video, now: Date): boolean => {
  const e = tlEnd(v)
  return !!e && v.status !== STATUS_READY && e < now
}

/** A card dropped on a column: the new status, fitted to the video's Type (Blog: only Idea / Writing / Ready).
 *  null = nothing changes */
export function dropStatus(v: Video, status: string): string | null {
  const s = fixStatus(formatKind(v.format), status)
  return v.status !== s ? s : null
}
